<template>
  <b-card>
    <router-link
      class="flex items-center gap-[12px] text-[20px] w-fit hover:opacity-90 transition-opacity duration-300"
      aria-label="back"
      to="/report-performance/admin-marketplace"
      replace
    >
      <i
        class="bg-primary rounded-[8px] grid place-items-center text-white font-bold shrink-0 w-[40px] h-[40px] k-arrow-left-2"
      />
      <span class="text-[#222] font-semibold">Detail Report</span>
    </router-link>
    <div class="my-[32px] p-[18px] border-[1px] border-[#E5E7EB] rounded-[14px]">
      <div class="gap-[24px] grid grid-cols-[repeat(auto-fit,_minmax(160px,1fr))] xl:flex xl:justify-between">
        <div class="flex flex-col xl:basis-[280px]">
          <span class="text-[14px] text-[#6A7282]">Nama Talent</span>
          <b class="text-[16px] font-medium text-[#101828]">{{ performanceReport.talent_name || '-' }}</b>
        </div>
        <div class="flex flex-col xl:basis-[280px]">
          <span class="text-[14px] text-[#6A7282]">Partner</span>
          <b class="text-[16px] font-medium text-[#101828]">{{ performanceReport.partner_name || '-' }}</b>
          <small class="text-[12px] text-[#6A7282]">{{ performanceReport.partner_no || '-' }}</small>
        </div>
        <div class="flex flex-col xl:basis-[280px]">
          <span class="text-[14px] text-[#6A7282]">Talent Lead</span>
          <b class="text-[16px] font-medium text-[#101828]">{{ performanceReport.talent_lead || '-' }}</b>
        </div>
        <div class="flex flex-col xl:basis-[160px]">
          <span class="text-[14px] text-[#6A7282]">Periode</span>
          <b class="text-[16px] font-medium text-[#101828]">{{ performanceReport.period ? NODASH_MONTH_YEAR(performanceReport.period) : '-' }}</b>
        </div>
        <router-link
          v-if="performanceReport.assignment_id"
          :to="`/assignment-partners/${performanceReport.assignment_id}/show?open_completion=${performanceReport.talent_assignment_id}`"
          class="bg-[#F95031] w-fit hover:opacity-90 transition-opacity duration-300 rounded-[8px] h-[36px] text-white text-[14px] font-medium flex items-center px-[12px] gap-[16px]"
        >
          <i class="k-export text-[16ppx] shrink-0 font-bold" />
          Profile
        </router-link>
      </div>
      <router-link
        v-if="performanceReport.id"
        :to="`${$route.path}/preview`"
        target="_blank"
        class="text-[#08A0F7] flex gap-[8px] items-center text-[14px] mt-[16px] font-medium gap-[8px] w-fit transition-color duration-300"
      >
        <i class="k-document-text text-[16px] font-bold" />
        Link Report
        <i class="k-export text-[12px] font-bold" />
      </router-link>
    </div>
    <div
      v-if="performanceReport.revision_type === 'system'"
      class="p-[16px] flex items-start gap-[12px] rounded-[8px] border-[1px] border-[#FBA63C] bg-[#FFF8E1] mb-[32px]"
    >
      <img
        src="https://storage.googleapis.com/komerce/assets/svg/warning-Info-circle.svg"
        alt="warning"
        width="24"
        height="24"
        class="shrink-0"
      >
      <div class="flex flex-1 items-start gap-[12px] justify-between">
        <div>
          <b class="text-[16px] font-semibold text-[#333333]">Revisi Otomatis</b>
          <p class="text-[12px] text-[#333333] mt-[8px] mb-0">
            Beberapa data telah direvisi otomatis oleh sistem karena tidak valid. Silakan periksa kembali data.
          </p>
        </div>
        <button
          class="outline-none border-none"
          @click="performanceReport.revision_type = null"
        >
          <i class="k-close text-[32px] text-[#828282]" />
        </button>
      </div>
    </div>
    <div class="flex flex-wrap gap-y-[32px] gap-x-[20px]">
      <div class="grow basis-[440px] rounded-[14px] border-[1px] border-[#E2E2E2] p-[24px] flex flex-col gap-[16px] overflow-x-auto">
        <b class="text-[18px] font-medium text-[#101828]">Performa Talent</b>
        <small class="block mt-[8px] text-[12px] text-[#6A7282]">Insentif</small>
        <b-table
          :items="incentives"
          :fields="incentiveColumns"
          empty-text="Tidak ada data yang ditampilkan."
          responsive
          show-empty
          outlined
          small
          class="rounded-[12px] border-[1px] border-[#E5E7EB] mb-0"
          table-class="rounded-[10px] overflow-hidden"
        >
          <template #cell(value)="data">
            <span v-if="data.item.value">{{ data.item.value }}</span>
            <span
              v-else
              v-b-tooltip.hover.bottom="'Format input salah / tidak sesuai'"
              class="flex items-center justify-end gap-[10px] text-[#E31A1A] font-semibold text-[14px]"
            >
              <i class="k-danger text-[18px] font-bold" />
              ERROR
            </span>
          </template>
        </b-table>
        <div class="flex justify-between items-center gap-[16px]">
          <small class="text-[12px] text-[#6A7282]">Gaji Pokok</small>
          <b-button
            :disabled="isEditSalary || loading.workingDays"
            variant="outline-primary"
            size="sm"
            class="!flex items-center gap-[8px]"
            @click="isEditSalary = true"
          >
            <i class="k-edit-2 font-bold" />
            Edit Gaji Pokok
          </b-button>
        </div>
        <form @submit="handleUpdateBaseSalary">
          <b-table
            :items="baseSalaries"
            :fields="baseSalaryColumns"
            empty-text="Tidak ada data yang ditampilkan."
            responsive
            show-empty
            outlined
            thead-class="hidden"
            small
            class="rounded-[12px] border-[1px] border-[#E5E7EB] mb-0"
            table-class="rounded-[10px] overflow-hidden"
          >
            <template #cell(days)="data">
              <b-form-input
                :key="data.item.days + '-' + isEditSalary"
                type="text"
                class="w-full !border-dashed disabled:!border-none disabled:!bg-transparent disabled:text-right"
                placeholder="Jumlah hari"
                :disabled="!isEditSalary"
                :value="(!isEditSalary && !data.item.days) ? '-' : data.item.days"
                :name="workingDaysMap[data.item.key]"
                autocomplete="false"
                @keydown="preventNonNumericType"
                @paste="preventNonNumericPaste"
              />
            </template>
            <template #custom-foot>
              <tr>
                <td class="text-[#1E2939] text-[14px] !p-[16px] !border-t-0 bg-[#F8F8F8] rounded-bl-[12px] font-normal whitespace-nowrap">
                  Total hari kerja
                </td>
                <td class="text-[#1E2939] text-[14px] !p-[16px] !border-t-0 bg-[#F8F8F8] text-right rounded-br-[12px] font-normal">
                  <span class="px-[10px]">{{ workingDaysTotal }}</span>
                </td>
                <td class="!p-[16px] !border-t-0 bg-[#F8F8F8]" />
              </tr>
              <tr v-if="isEditSalary">
                <td
                  colspan="3"
                  align="right"
                  class="w-full gap-[12px] !py-[12px] !px-[16px]"
                >
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    type="button"
                    class="mr-[12px]"
                    @click="isEditSalary = false"
                  >
                    Batal
                  </b-button>
                  <b-button
                    variant="primary"
                    size="sm"
                    type="submit"
                    class="whitespace-nowrap"
                    :disabled="loading.workingDays"
                  >
                    <b-spinner
                      v-if="loading.workingDays"
                      small
                    />
                    Simpan Data
                  </b-button>
                </td>
              </tr>
            </template>
          </b-table>
        </form>
        <small class="block mt-[8px] text-[12px] text-[#6A7282]">Lain-lain</small>
        <b-table
          :items="others"
          :fields="otherColumns"
          empty-text="Tidak ada data yang ditampilkan."
          responsive
          show-empty
          outlined
          thead-class="hidden"
          small
          class="rounded-[12px] border-[1px] border-[#E5E7EB] mb-0"
          table-class="rounded-[10px] overflow-hidden"
        >
          <template #cell(nominal)="data">
            <span
              v-if="data.item.nominal"
              class="whitespace-nowrap"
            >{{ IDR(data.item.nominal) }}</span>
            <span
              v-else
              v-b-tooltip.hover.bottom="'Format input salah / tidak sesuai'"
              class="flex items-center justify-end gap-[10px] text-[#E31A1A] font-semibold text-[14px]"
            >
              <i class="k-danger text-[18px] font-bold" />
              ERROR
            </span>
          </template>
        </b-table>
      </div>
      <div class="grow basis-[320px] lg:max-w-[380px]">
        <div class="rounded-[14px] border-[1px] border-[#E2E2E2] p-[24px] flex flex-col gap-[16px] mb-[32px]">
          <b class="text-[18px] font-medium text-[#101828] block py-[2px]">Detail Insentif</b>
          <div class="flex flex-col gap-[16px]">
            <div
              v-for="value, key in incentiveDetails.items"
              :key="key"
              class="h-[60px] px-[20px] flex justify-between items-center text-[14px] text-[#364153] bg-[#FFECE9] rounded-[14px] border-l-[4px] border-l-[#FF6A3A]"
            >
              <span>{{ key }}</span>
              <span>{{ value }}</span>
            </div>
          </div>
          <hr class="border-[#E5E7EB] border-[2px]">
          <div class="h-[68px] px-[20px] bg-[#FCD4BE] text-[#333333] flex justify-between items-center rounded-[14px]">
            <span class="text-[14px]">Total</span>
            <b class="text-[18px]">{{ incentiveDetails.total }}</b>
          </div>
        </div>
        <v-select
          v-model="reportStatus"
          label="text"
          :reduce="option => option.value"
          :options="reportStatusOptions"
          :clearable="false"
          :disabled="disabledReportStatus"
          class="[&>div]:h-[48px] [&>div]:!rounded-[12px] [&>div]:border-[1px] [&>div]:border-[#F95031] [&>div:has(:disabled)]:border-[#C2C2C2]
          [&>div:has(:disabled)]:bg-white [&_span]:text-[16px] [&_span]:font-semibold [&_span]:!text-[#F95031] [&>div:has(:disabled)_span]:!text-[#C2C2C2]
          [&_.open-indicator]:h-[28px] [&_.open-indicator]:w-[28px] [&_.open-indicator]:text-[#F95031] [&>div:has(:disabled)_.open-indicator]:!text-[#C2C2C2]"
        />
        <div
          v-if="reportStatus === 'revision'"
          class="bg-[#F8F8F8] p-[12px] rounded-[12px] mt-[32px]"
        >
          <span class="text-black text-[14px]">Catatan revisi:</span>
          <p class="my-[10px] text-[12px] text-[#828282]">
            Catatan revisi akan dikirim ke talent sebagai bahan perbaikan.
          </p>
          <b-form-textarea
            id="revision"
            v-model="performanceReport.revision_note"
            placeholder="Masukkan catatan..."
            rows="5"
            class="!rounded-[12px] disabled:!bg-white"
            :disabled="disabledReportStatus"
            maxlength="500"
          />
          <span class="text-[12px] mt-[4px] block text-right text-[#828282]">{{ performanceReport.revision_note.length }}/500</span>
        </div>
        <b-button
          v-if="showedSubmitButton"
          variant="primary"
          size="lg"
          class="mt-[40px] w-full !rounded-[12px]"
          :disabled="loading.submit"
          @click="$bvModal.show('save-report')"
        >
          <b-spinner
            v-if="loading.submit"
            small
          />
          Simpan</b-button>
      </div>
    </div>
    <ConfirmPopup
      id="save-report"
      title="Simpan Data"
      text="Data akan disimpan ke dalam database. Pastikan data sudah benar sebelum disimpan."
      confirm-text="Submit"
      cancel-text="Batal"
      @confirm="submitPerformanceReport"
    />
  </b-card>
</template>

<script>
import { IDR } from '@/libs/currency'
import vSelect from 'vue-select'
import { NODASH_MONTH_YEAR } from '@/libs/filterDate'
import { komtimAxiosIns } from '@/libs/axios'
import {
  baseSalaryColumns, incentiveColumns, otherColumns, reportStatusOptions, workingDaysMap,
} from './config'
import ConfirmPopup from '../../../training/training-program/ConfirmPopup.vue'

export default {
  components: { vSelect, ConfirmPopup },
  data() {
    return {
      IDR,
      NODASH_MONTH_YEAR,
      workingDaysMap,
      incentiveColumns,
      baseSalaryColumns,
      otherColumns,
      performanceReport: {},
      incentives: [],
      baseSalaries: [],
      others: [],
      isEditSalary: false,
      reportStatus: 'submitted',
      reportStatusOptions,
      loading: {
        submit: false,
        workingDays: false,
      },
    }
  },
  computed: {
    incentiveDetails() {
      const incentiveTotal = this.incentives.reduce((acc, incentive) => acc + (incentive.nominal || 0), 0)
      const otherTotal = this.others.reduce((acc, other) => acc + (other.nominal || 0), 0)

      return {
        items: {
          'Biaya Admin': IDR(this.performanceReport.admin_fee),
          'Gaji pokok': IDR(this.performanceReport.base_salary),
          Insentif: IDR(incentiveTotal),
          'Lain-lain': IDR(otherTotal),
        },
        total: IDR(this.performanceReport.admin_fee + incentiveTotal + this.performanceReport.base_salary + otherTotal),
      }
    },
    workingDaysTotal() {
      return this.baseSalaries.reduce((acc, salary) => acc + (salary.days || 0), 0)
    },
    disabledReportStatus() {
      return ['revision', 'verified'].includes(this.reportStatus) && ['revision', 'verified'].includes(this.performanceReport.status)
    },
    showedSubmitButton() {
      return ['revision', 'verified'].includes(this.reportStatus) && this.performanceReport.status === 'submitted'
    },
  },
  mounted() {
    this.getPerformanceReport()
  },
  methods: {
    preventNonNumericType(e) {
      const charCode = e.which ? e.which : e.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 8 && charCode !== 37 && charCode !== 39) {
        e.preventDefault()
      }
    },
    preventNonNumericPaste(e) {
      const pastedData = e.clipboardData.getData('text')
      if (!/^\d*$/.test(pastedData)) {
        e.preventDefault()
      }
    },
    async getPerformanceReport() {
      this.loading.submit = true
      this.loading.workingDays = true
      try {
        const { data: { data } } = await komtimAxiosIns.get(`v1/admin_marketplace_performance/report/${this.$route.params.id}`)
        this.performanceReport = data
        this.incentives = data.incentives
        this.baseSalaries = data.base_salaries
        this.others = data.others
        this.reportStatus = data.status
      } catch (error) {
        this.alertError(error)
      } finally {
        this.loading.submit = false
        this.loading.workingDays = false
      }
    },
    async handleUpdateBaseSalary(e) {
      e.preventDefault()
      if (this.loading.workingDays) return
      this.loading.workingDays = true
      try {
        const form = new FormData(e.target)
        const body = Object.fromEntries(Array.from(form.entries()).map(([key, value]) => [key, Number(value.replace(/\D/g, ''))]))
        const { data: { data } } = await komtimAxiosIns.patch(`v1/admin_marketplace_performance/report/${this.$route.params.id}/base_salary`, body)

        this.baseSalaries = data.base_salary_details
        this.performanceReport.base_salary = data.base_salary
      } catch (error) {
        this.alertError(error)
      } finally {
        this.isEditSalary = false
        this.loading.workingDays = false
      }
    },
    async submitPerformanceReport() {
      if (this.loading.submit) return
      this.$bvModal.hide('save-report')
      this.loading.submit = true
      try {
        const data = {
          status: this.reportStatus,
          revision_note: this.performanceReport.revision_note,
        }
        await komtimAxiosIns.patch(`v1/admin_marketplace_performance/report/${this.$route.params.id}/status`, data)
        this.performanceReport.status = data.status
      } catch (error) {
        this.alertError(error)
      } finally {
        this.loading.submit = false
      }
    },
  },
}
</script>
